/**
 * API клиент для работы с сотрудниками (Employees)
 */

import { api } from '../../../services/api';
import type {
  Employee,
  EmployeeListResponse,
  EmployeeFilter,
  CreateEmployeeRequest,
  UpdateEmployeeRequest,
  EmployeeDetail,
  EmployeeInventoryResponse,
  EmployeeContactInfo,
  UpdateEmployeeContactRequest,
  EmployeeHistoryResponse,
  EmployeeHistoryFilter,
  EmployeeTimesheetResponse,
  EmployeeTimesheetFilter,
  EmployeeEditResponse,
  UpdateEmployeeEditRequest,
  GlobalRight,
  V5PermissionCatalogEntry,
  TimesheetUpdateRequest,
  AbsenceRequest,
  WorkSessionStartRequest,
  WorkSessionFinishRequest,
} from '../types';

export const usersApi = {
  /**
   * Получить список сотрудников с пагинацией и фильтрацией
   */
  list: async (filters: EmployeeFilter): Promise<EmployeeListResponse> => {
    
    // Явная сериализация параметров (axios может игнорировать false)
    const params: Record<string, string | number | boolean> = {};
    
    if (filters.page) params.page = filters.page;
    if (filters.limit) params.limit = filters.limit;
    if (filters.sort_by) params.sort_by = filters.sort_by;
    if (filters.sort_desc !== undefined) params.sort_desc = filters.sort_desc;
    if (filters.query) params.query = filters.query;
    
    // ✅ КРИТИЧНО: явно передаем is_active (даже если false!)
    if (filters.is_active !== undefined) {
      params.is_active = filters.is_active;
    }
    
    
    const response = await api.get<EmployeeListResponse>('/employees', { params });
    return response.data;
  },

  /**
   * Получить сотрудника по ID
   */
  getById: async (id: number): Promise<EmployeeDetail> => {
    const response = await api.get<EmployeeDetail>(`/employees/${id}`);
    return response.data;
  },

  /**
   * Создать нового сотрудника
   */
  create: async (data: CreateEmployeeRequest): Promise<Employee> => {
    const response = await api.post<Employee>('/employees', data);
    return response.data;
  },

  /**
   * Обновить данные сотрудника
   */
  update: async (id: number, data: UpdateEmployeeRequest): Promise<Employee> => {
    const response = await api.put<Employee>(`/employees/${id}`, data);
    return response.data;
  },

  /**
   * Удалить сотрудника
   */
  delete: async (id: number): Promise<void> => {
    await api.delete(`/employees/${id}`);
  },

  /**
   * Получить ТМЦ сотрудника
   */
  getInventory: async (id: number): Promise<EmployeeInventoryResponse> => {
    const response = await api.get<EmployeeInventoryResponse>(`/users/${id}/inventory`);
    return response.data;
  },

  /**
   * Получить контакты сотрудника
   */
  getContact: async (id: number): Promise<EmployeeContactInfo> => {
    const response = await api.get<EmployeeContactInfo>(`/users/${id}/contact`);
    return response.data;
  },

  /**
   * Обновить контакты сотрудника
   */
  updateContact: async (
    id: number,
    data: UpdateEmployeeContactRequest,
  ): Promise<EmployeeContactInfo> => {
    const response = await api.put<EmployeeContactInfo>(`/users/${id}/contact`, data);
    return response.data;
  },

  /**
   * Получить историю действий сотрудника
   */
  getHistory: async (
    id: number,
    filter: EmployeeHistoryFilter,
  ): Promise<EmployeeHistoryResponse> => {
    const params: Record<string, string | number> = {
      date_from: filter.date_from,
      date_to: filter.date_to,
    };

    if (filter.type_id) params.type_id = filter.type_id;
    if (filter.object_id) params.object_id = filter.object_id;
    if (filter.page) params.page = filter.page;
    if (filter.limit) params.limit = filter.limit;

    const response = await api.get<EmployeeHistoryResponse>(`/users/${id}/history`, { params });
    return response.data;
  },

  /**
   * Получить табель сотрудника
   */
  getTimesheet: async (
    id: number,
    filter: EmployeeTimesheetFilter,
  ): Promise<EmployeeTimesheetResponse> => {
    const params: Record<string, string> = {
      date_from: filter.date_from,
      date_to: filter.date_to,
    };
    const response = await api.get<EmployeeTimesheetResponse>(`/users/${id}/timesheet`, { params });
    return response.data;
  },

  /**
   * Массовое обновление табеля
   */
  updateTimesheet: async (id: number, payload: TimesheetUpdateRequest): Promise<void> => {
    await api.post(`/users/${id}/timesheet`, payload);
  },

  /**
   * Создать период отсутствия
   */
  createAbsence: async (id: number, payload: AbsenceRequest): Promise<void> => {
    await api.post(`/users/${id}/absences`, payload);
  },

  /**
   * Удалить период отсутствия
   */
  deleteAbsence: async (id: number, payload: AbsenceRequest): Promise<void> => {
    await api.delete(`/users/${id}/absences`, { data: payload });
  },

  /**
   * Запустить смену
   */
  startWorkSession: async (id: number, payload: WorkSessionStartRequest): Promise<void> => {
    await api.post(`/users/${id}/work-sessions/start`, payload);
  },

  /**
   * Завершить смену
   */
  finishWorkSession: async (id: number, payload: WorkSessionFinishRequest): Promise<void> => {
    await api.post(`/users/${id}/work-sessions/finish`, payload);
  },

  /**
   * Получить данные для редактирования сотрудника
   */
  getEmployeeEdit: async (id: number): Promise<EmployeeEditResponse> => {
    const response = await api.get<EmployeeEditResponse>(`/users/${id}/edit`);
    return response.data;
  },

  /**
   * Обновить данные сотрудника
   */
  updateEmployeeEdit: async (id: number, data: UpdateEmployeeEditRequest): Promise<void> => {
    await api.put(`/users/${id}/edit`, data);
  },

  /**
   * Получить справочник глобальных прав
   */
  getGlobalRights: async (): Promise<GlobalRight[]> => {
    const response = await api.get<GlobalRight[]>('/users/rights/global');
    return response.data;
  },

  /**
   * Получить каталог V5-прав для секции выдачи (Ф2). grantable — держит ли
   * текущий пользователь право сам (R1). RoleOnly-права каталог не отдаёт.
   */
  getV5Permissions: async (): Promise<V5PermissionCatalogEntry[]> => {
    const response = await api.get<V5PermissionCatalogEntry[]>('/users/rights/v5');
    return response.data;
  },
};

